import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export interface HeroContent {
  label: string;
  headline: string;
  headlineAccent?: string;
  subheadline: string;
  primaryCTA: { label: string; href: string };
  secondaryCTA: { label: string; href: string };
  backgroundImage: string;
  stats?: Array<{ value: string; label: string }>;
}

interface Props {
  content: HeroContent;
}

export default function HeroSection({ content }: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full overflow-hidden" style={{ minHeight: '100svh' }}>
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={content.backgroundImage}
          alt="Luxury landscaping by CQC"
          className="w-full h-full object-cover object-center scale-105 transition-transform duration-[8000ms]"
          style={{ transform: visible ? 'scale(1)' : 'scale(1.05)' }}
        />
        {/* Cinematic dark overlay */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, rgba(7,20,10,0.97) 0%, rgba(7,20,10,0.82) 45%, rgba(7,20,10,0.35) 100%)' }}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#07140a]/90 via-transparent to-[#07140a]/30"></div>
      </div>

      {/* Thin gold left accent */}
      <div className="absolute left-0 inset-y-0 w-[3px] bg-gradient-to-b from-transparent via-gold to-transparent opacity-50"></div>

      {/* Content — vertically centered */}
      <div className="relative z-10 flex flex-col justify-center min-h-screen px-8 lg:px-16 xl:px-24 pt-24 pb-16">
        <div className={`max-w-3xl transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

          {/* Label */}
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-12 bg-gold"></div>
            <span className="font-sans text-gold text-[10px] tracking-[0.3em] uppercase font-medium">{content.label}</span>
          </div>

          {/* Headline */}
          <h1
            className="font-serif text-white font-semibold leading-[0.95] tracking-tight mb-8"
            style={{ fontSize: 'clamp(3.8rem, 8vw, 7rem)' }}
          >
            {content.headline}
            {content.headlineAccent && (
              <>
                <br />
                <em className="text-gold italic">{content.headlineAccent}</em>
              </>
            )}
          </h1>

          {/* Subheadline */}
          <p
            className="font-sans text-white/60 leading-relaxed mb-10 max-w-lg"
            style={{ fontSize: 'clamp(1rem, 1.5vw, 1.15rem)' }}
          >
            {content.subheadline}
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4 mb-20">
            <Link
              to={content.primaryCTA.href}
              className="whitespace-nowrap inline-flex items-center gap-2.5 bg-gold hover:bg-gold-light text-forest-deep font-bold text-sm tracking-wide rounded-full px-8 py-4 transition-all duration-200 cursor-pointer shadow-xl shadow-gold/25"
            >
              {content.primaryCTA.label}
              <i className="ri-arrow-right-line"></i>
            </Link>
            <a
              href={content.secondaryCTA.href}
              className="whitespace-nowrap inline-flex items-center gap-2.5 border border-white/35 text-white hover:bg-white hover:text-forest font-semibold text-sm tracking-wide rounded-full px-8 py-4 transition-all duration-200 cursor-pointer backdrop-blur-sm"
            >
              <i className="ri-phone-fill text-gold text-sm"></i>
              {content.secondaryCTA.label}
            </a>
          </div>

          {/* Stats */}
          {content.stats && content.stats.length > 0 && (
            <div className="flex flex-wrap gap-10 pt-8 border-t border-white/10">
              {content.stats.map((s, i) => (
                <div key={i}>
                  <div className="font-serif text-4xl text-gold leading-none font-semibold">{s.value}</div>
                  <div className="font-sans text-white/35 text-[10px] tracking-[0.15em] uppercase mt-1.5">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-10 hidden lg:flex flex-col items-center gap-2 opacity-40 hover:opacity-70 transition-opacity">
        <div className="w-px h-14 bg-gradient-to-b from-white to-transparent"></div>
        <span className="font-sans text-[9px] text-white tracking-[0.3em] uppercase" style={{ writingMode: 'vertical-rl' }}>Explore</span>
      </div>
    </section>
  );
}
